/**
 * Authentication Slice
 * Manages user authentication state, tenant context, and permissions
 *
 * Storage Strategy:
 * - Tokens (access_token, refresh_token): httpOnly cookies (handled by server)
 * - User data, permissions: Redux memory (in-memory only, lost on page close)
 * - Tenant ID: sessionStorage (survives page reload, cleared on tab close)
 *
 * This approach protects tokens from XSS while maintaining user experience.
 */

import { createSlice, createAsyncThunk, createSelector } from '@reduxjs/toolkit';
import djangoClient, { parseDjangoError, clearAuth } from '../../shared/api/djangoClient';
// R4: resetApiState.js imports all 18 RTK Query API slices, which are
// (transitively, via shared/api/baseQuery.js) built on top of this same
// app layer -- a static top-level import here pulls that entire surface
// into every module that imports authSlice (very broad: ProtectedRoute,
// most page components, ...). Import it dynamically inside the thunk
// instead, deferring resolution to the (runtime-only) logout call and
// matching the same fix applied to shared/api/baseQuery.js's two
// forced-logout branches.

// Restore tenant_id from sessionStorage on page load (survives page refresh)
const _storedTenantId = (() => {
  try {
    return sessionStorage.getItem('tenant_id');
  } catch {
    return null;
  }
})();

// Initial state - no token or user data read from storage
// Tokens are in httpOnly cookies, user/permissions restored via API call
const initialState = {
  user: null,
  tenantId: _storedTenantId || null,
  permissions: [],
  isAuthenticated: false,
  tenant: null,
  // Start in loading state to avoid protected-route redirect races
  // before fetchCurrentUser restores cookie-backed sessions.
  isLoading: true,
  error: null,
  // MFA pending state — set when server requires a second factor
  mfaPending: false,
  mfaPartialToken: null,
};

/**
 * Async thunk: Login user
 */
export const login = createAsyncThunk(
  'auth/login',
  async ({ email, password, tenantId }, { rejectWithValue }) => {
    try {
      const response = await djangoClient.post('/api/v1/auth/login/', {
        email,
        password,
        ...(tenantId ? { tenant_id: tenantId } : {}),
      });

      // Response contains user, permissions, tenant (NOT tokens - they're in httpOnly cookies)
      // OR an mfa_required indicator with a partial_token
      const data = response.data;

      if (data.mfa_required) {
        return { mfaRequired: true, partialToken: data.partial_token };
      }

      const { user, permissions, tenant } = data;

      // Store tenant_id in sessionStorage (survives page refresh)
      const resolvedTenantId = tenant?.id || tenantId;
      if (resolvedTenantId) {
        sessionStorage.setItem('tenant_id', resolvedTenantId);
      }

      return {
        user,
        tenantId: resolvedTenantId,
        permissions: permissions || [],
        tenant,
      };
    } catch (error) {
      return rejectWithValue(parseDjangoError(error));
    }
  }
);

/**
 * Async thunk: Logout user
 */
export const logout = createAsyncThunk('auth/logout', async (_, { dispatch, rejectWithValue }) => {
  try {
    await djangoClient.post('/api/v1/auth/logout/');
    // Clear auth and storage
    sessionStorage.removeItem('tenant_id');
    clearAuth();
    // R4: clear every RTK Query cache slice so the next user/session
    // never sees a stale cached response from this one.
    const { resetAllApiState } = await import('../../shared/api/resetApiState');
    resetAllApiState(dispatch);
    return null;
  } catch (error) {
    // Clear auth anyway on logout error
    sessionStorage.removeItem('tenant_id');
    clearAuth();
    const { resetAllApiState } = await import('../../shared/api/resetApiState');
    resetAllApiState(dispatch);
    return rejectWithValue(parseDjangoError(error));
  }
});

/**
 * Async thunk: Fetch current user
 * Used on app initialization to restore auth state from valid cookie session
 */
export const fetchCurrentUser = createAsyncThunk(
  'auth/fetchCurrentUser',
  async (_, { rejectWithValue }) => {
    try {
      const response = await djangoClient.get('/api/v1/auth/users/me/');
      const user = response.data;

      // Extract permissions and tenant from user response
      const permissions = user.permissions || [];
      const tenantId = user.tenant?.id || sessionStorage.getItem('tenant_id');

      // Store tenant_id in sessionStorage
      if (tenantId) {
        sessionStorage.setItem('tenant_id', tenantId);
      }

      return {
        user,
        tenantId,
        permissions,
        tenant: user.tenant,
      };
    } catch (error) {
      return rejectWithValue(parseDjangoError(error));
    }
  }
);

/**
 * Async thunk: Login with token (for passwordless auth)
 */
export const loginWithToken = createAsyncThunk(
  'auth/loginWithToken',
  async (authResult, { rejectWithValue }) => {
    try {
      const { user, permissions, tenant } = authResult;

      // Store tenant_id in sessionStorage
      const tenantId = tenant?.id;
      if (tenantId) {
        sessionStorage.setItem('tenant_id', tenantId);
      }

      return {
        user,
        tenantId,
        permissions,
        tenant,
      };
    } catch (error) {
      return rejectWithValue(error.message || 'Token login failed');
    }
  }
);

/**
 * Async thunk: Verify OTP and complete login (primary passwordless login method)
 */
export const verifyOtpLogin = createAsyncThunk(
  'auth/verifyOtpLogin',
  async ({ email, otp_code }, { rejectWithValue }) => {
    try {
      const response = await djangoClient.post('/api/v1/auth/otp/verify/', {
        email,
        otp_code,
      });

      const data = response.data;

      // IDN-04: mirror login()'s mfa_required branch. If the tenant requires
      // MFA past its grace period, the server now sends a partial token here
      // (same as password login) instead of a completed session — without
      // this branch the partial token was destructured away and the state
      // below was populated with undefined user/tenant while isAuthenticated
      // was set true by the fulfilled reducer, silently bypassing MFA.
      if (data.mfa_required) {
        return { mfaRequired: true, partialToken: data.partial_token };
      }

      const { user, permissions, tenant } = data;

      // Store tenant_id in sessionStorage
      if (tenant?.id) {
        sessionStorage.setItem('tenant_id', tenant.id);
      }

      return {
        user,
        tenantId: tenant?.id,
        permissions: permissions || [],
        tenant,
      };
    } catch (error) {
      return rejectWithValue(parseDjangoError(error));
    }
  }
);

/**
 * Async thunk: Update user profile
 */
export const updateProfile = createAsyncThunk(
  'auth/updateProfile',
  async (profileData, { getState, rejectWithValue }) => {
    try {
      const userId = getState().auth.user?.id;

      if (!userId) {
        return rejectWithValue('Unable to determine the current user profile.');
      }

      const response = await djangoClient.patch(`/api/v1/auth/users/${userId}/`, profileData);
      const user = response.data;

      return user;
    } catch (error) {
      return rejectWithValue(parseDjangoError(error));
    }
  }
);

// Auth slice
const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    // Initialize auth state from valid cookie session
    // Called on app load to restore user state from server
    initializeAuth: (state) => {
      // Restore tenant_id from sessionStorage if available
      const tenantId = sessionStorage.getItem('tenant_id');
      if (tenantId) {
        state.tenantId = tenantId;
      }
      // User/permissions will be restored via fetchCurrentUser API call or login
    },
    // Clear auth state
    clearAuthState: (state) => {
      state.user = null;
      state.tenantId = null;
      state.permissions = [];
      state.tenant = null;
      state.isAuthenticated = false;
      state.error = null;
      sessionStorage.removeItem('tenant_id');
      clearAuth();
    },
    // Set permissions
    setPermissions: (state, action) => {
      state.permissions = action.payload;
    },
    // Clear error
    clearError: (state) => {
      state.error = null;
    },
    // Set MFA pending state — dispatched when login returns mfa_required: true
    mfaRequired: (state, action) => {
      state.mfaPending = true;
      state.mfaPartialToken = action.payload.partialToken;
      state.isLoading = false;
      state.error = null;
    },
    // Clear MFA pending state (on cancel or success)
    clearMfa: (state) => {
      state.mfaPending = false;
      state.mfaPartialToken = null;
    },
    // Set auth on signup completion
    setAuthComplete: (state, action) => {
      state.isAuthenticated = true;
      state.user = action.payload.user;
      state.tenantId = action.payload.tenantId;
      state.permissions = action.payload.permissions || [];
      if (state.tenantId) {
        sessionStorage.setItem('tenant_id', state.tenantId);
      }
    },
  },
  extraReducers: (builder) => {
    // Login
    builder
      .addCase(login.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.isLoading = false;
        if (action.payload.mfaRequired) {
          // Server requires a second factor — go to MFA verify page
          state.mfaPending = true;
          state.mfaPartialToken = action.payload.partialToken;
          return;
        }
        state.isAuthenticated = true;
        state.user = action.payload.user;
        state.tenantId = action.payload.tenantId;
        state.permissions = action.payload.permissions;
        state.tenant = action.payload.tenant || null;
        state.mfaPending = false;
        state.mfaPartialToken = null;
      })
      .addCase(login.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
        state.isAuthenticated = false;
      });

    // Login with token (passwordless)
    builder
      .addCase(loginWithToken.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(loginWithToken.fulfilled, (state, action) => {
        state.tenant = action.payload.tenant || null;
        state.isLoading = false;
        state.isAuthenticated = true;
        state.user = action.payload.user;
        state.tenantId = action.payload.tenantId;
        state.permissions = action.payload.permissions;
      })
      .addCase(loginWithToken.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
        state.isAuthenticated = false;
      });

    // OTP Login
    builder
      .addCase(verifyOtpLogin.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(verifyOtpLogin.fulfilled, (state, action) => {
        state.isLoading = false;
        if (action.payload.mfaRequired) {
          // IDN-04: server requires a second factor — go to MFA verify page,
          // same as login.fulfilled. Do NOT set isAuthenticated.
          state.mfaPending = true;
          state.mfaPartialToken = action.payload.partialToken;
          return;
        }
        state.tenant = action.payload.tenant || null;
        state.isAuthenticated = true;
        state.user = action.payload.user;
        state.tenantId = action.payload.tenantId;
        state.permissions = action.payload.permissions;
        state.mfaPending = false;
        state.mfaPartialToken = null;
      })
      .addCase(verifyOtpLogin.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
        state.isAuthenticated = false;
      });

    // Logout
    builder
      .addCase(logout.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(logout.fulfilled, (state) => {
        state.isLoading = false;
        state.isAuthenticated = false;
        state.user = null;
        state.tenantId = null;
        state.permissions = [];
      })
      .addCase(logout.rejected, (state) => {
        state.isLoading = false;
        state.isAuthenticated = false;
        state.user = null;
        state.tenantId = null;
        state.permissions = [];
      });

    // Fetch current user
    builder
      .addCase(fetchCurrentUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchCurrentUser.fulfilled, (state, action) => {
        state.tenant = action.payload.tenant || null;
        state.tenant = null;
        state.tenant = null;
        state.isLoading = false;
        state.isAuthenticated = true;
        state.user = action.payload.user;
        state.tenantId = action.payload.tenantId;
        state.permissions = action.payload.permissions;
      })
      .addCase(fetchCurrentUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
        state.isAuthenticated = false;
      });

    // Update profile
    builder
      .addCase(updateProfile.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateProfile.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload;
      })
      .addCase(updateProfile.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

// Selectors
const selectAuthState = (state) => state.auth;
export const selectAuth = createSelector([selectAuthState], (auth) => ({
  user: auth.user,
  tenant: auth.tenant || auth.user?.tenant || null,
  isAuthenticated: auth.isAuthenticated,
  tenantId: auth.tenantId,
  permissions: auth.permissions,
  isLoading: auth.isLoading,
  error: auth.error,
}));
export const selectUser = (state) => state.auth.user;
export const selectIsAuthenticated = (state) => state.auth.isAuthenticated;
export const selectTenantId = (state) => state.auth.tenantId;
export const selectTenant = (state) => state.auth.tenant || state.auth.user?.tenant || null;
export const selectPermissions = (state) => state.auth.permissions;
export const selectAuthLoading = (state) => state.auth.isLoading;
export const selectAuthError = (state) => state.auth.error;

// Check if user has specific permission
export const selectHasPermission = (state, permission) => {
  return state.auth.permissions.includes(permission);
};

// Check if user has any of the specified permissions
export const selectHasAnyPermission = (state, permissions) => {
  return permissions.some((permission) => state.auth.permissions.includes(permission));
};

// Check if user has all specified permissions
export const selectHasAllPermissions = (state, permissions) => {
  return permissions.every((permission) => state.auth.permissions.includes(permission));
};

export const {
  initializeAuth,
  clearAuthState,
  setPermissions,
  clearError,
  setAuthComplete,
  mfaRequired,
  clearMfa,
} = authSlice.actions;

export default authSlice.reducer;
